import config from '../common/config';
import { ITokenResponse, IUser } from '../models/auth';
import { clearLastLocation } from '../common/auth-redirect';
import axios, { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment';

export interface IAuthService {
  loadFromCookieStore():void
  login(emailAddress:string, password: string):Promise<ITokenResponse>
  refreshToken():Promise<void>
  storeLogin(tokenResponse:ITokenResponse):void
  clearLogin():void
  logout():void
  currentUser(): IUser | null
  loggedIn():boolean
}

type TokenResponse = AxiosResponse<ITokenResponse['data']>;

function isAxiosError(value: unknown): value is AxiosError {
  return typeof value === 'object' && value !== null && 'isAxiosError' in value;
}

function parseStoredUser(): IUser | null {
  const raw = localStorage.getItem('client');
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as IUser;
  } catch (e) {
    console.log('AuthService: Failed to parse stored auth user', e);
    return null;
  }
}

export function hasPermission(user: IUser | null | undefined, permission: string): boolean {
  return user?.permissions?.[permission] === true;
}

export function hasOneOfPermissions(user: IUser | null | undefined, permissions: string[]): boolean {
  return permissions.some((p) => hasPermission(user, p));
}

export function hydrateUser(user: IUser): IUser {
  const permissions: { [id: string]: boolean } = (user.roles ?? [])
    .map((r) => {
      const rolePermissions = { ...(r?.permissions ?? {}) };
      // Remove any false permissions to avoid them overwriting true permissions.
      for (const k in rolePermissions) {
        if (rolePermissions[k] === false) {
          delete rolePermissions[k];
        }
      }
      return rolePermissions;
    })
    .reduce((prev, cur) => Object.assign({}, prev, cur), {});

  user.permissions = permissions;
  user.hasPermission = function (permission: string) {
    return hasPermission(this, permission);
  };
  user.hasOneOfPermissions = function (permissionsList: string[]) {
    return hasOneOfPermissions(this, permissionsList);
  };
  return user;
}

function storedAccessTokenExpiryIsValid(): boolean {
  const rawExpiry = localStorage.getItem('tokenExpiry');
  if (!rawExpiry) {
    return false;
  }
  const expiryMs = new Date(rawExpiry).getTime();
  if (!Number.isFinite(expiryMs)) {
    return false;
  }
  return expiryMs > Date.now();
}

function readStoredUser(): IUser | null {
  const user = parseStoredUser();
  if (user == null) {
    return null;
  }
  return hydrateUser(user);
}

function isTokenNotExpired(): boolean {
  return storedAccessTokenExpiryIsValid();
}

export function createAuthService(): IAuthService {
  class Auth implements IAuthService {
    private refreshing = false;
    private cachedUser: IUser | null = null;

    constructor() {
      this.loadFromCookieStore();
    }

    loadFromCookieStore(): void {
      if (localStorage.getItem('token') == null) {
        this.cachedUser = null;
        return;
      }

      this.cachedUser = readStoredUser();
    }

    async login(emailAddress: string, password: string): Promise<ITokenResponse> {
      console.log(`AuthService: Attempting login for ${emailAddress}`);
      this.clearLogin();

      try {
        const response: TokenResponse = await axios.post(`${config.api.url}/auth/token`, {
          grant_type: 'password',
          client_id: config.api.client.id,
          client_secret: config.api.client.secret,
          username: `StaffUser|${emailAddress}`,
          password,
        });

        const tokenResponse: ITokenResponse = { data: response.data };
        if (tokenResponse.data.staffUser) {
          hydrateUser(tokenResponse.data.staffUser);
        }
        return tokenResponse;
      } catch (err: unknown) {
        if (isAxiosError(err) && err.response?.data) {
          const data = err.response.data as unknown;
          return Promise.reject({ data });
        }
        return Promise.reject(err);
      }
    }

    refreshToken(): Promise<void> {
      if (isTokenNotExpired()) {
        return Promise.resolve();
      }

      if (this.refreshing) {
        return new Promise<void>((resolve, reject) => {
          setTimeout(() => {
            this.refreshToken().then(resolve).catch(reject);
          }, 100);
        });
      }

      this.refreshing = true;
      return axios
        .post(`${config.api.url}/auth/token`, {
          grant_type: 'refresh_token',
          client_id: 'relcore',
          refresh_token: localStorage.getItem('refreshToken'),
        })
        .then((response: TokenResponse) => {
          this.refreshing = false;

          const tokenResponse: ITokenResponse = { data: response.data };
          if (tokenResponse.data.error != null) {
            this.logout();
            return;
          }

          hydrateUser(tokenResponse.data.staffUser);
          this.storeLogin(tokenResponse);
        })
        .catch(() => {
          this.refreshing = false;
          this.logout();
        });
    }

    storeLogin(tokenResponse: ITokenResponse): void {
      console.log('AuthService: Storing login');
      hydrateUser(tokenResponse.data.staffUser);
      localStorage.setItem('token', tokenResponse.data.access_token);
      localStorage.setItem(
        'tokenExpiry',
        moment().add(tokenResponse.data.expires_in, 'seconds').toISOString()
      );
      localStorage.setItem('refreshToken', tokenResponse.data.refresh_token);
      localStorage.setItem('client', JSON.stringify(tokenResponse.data.staffUser));
      localStorage.setItem('tenantTimezone', tokenResponse.data.tenantTimezone);
      localStorage.setItem('systemTimezone', tokenResponse.data.systemTimezone);
      this.cachedUser = tokenResponse.data.staffUser;
    }

    clearLogin(): void {
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiry');
      localStorage.removeItem('client');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('systemTimezone');
      localStorage.removeItem('tenantTimezone');
      this.cachedUser = null;
    }

    logout(): void {
      console.log('AuthService: Logging out');
      // Explicit logout should not retain a post-login return path.
      clearLastLocation();
      this.clearLogin();
      window.location.href = '/login';
    }

    currentUser(): IUser | null {
      if (this.cachedUser != null) {
        return this.cachedUser;
      }
      const user = readStoredUser();
      if (user != null) {
        this.cachedUser = user;
        return this.cachedUser;
      }
      return null;
    }

    loggedIn(): boolean {
      const token = localStorage.getItem('token');
      if (!token) {
        return false;
      }
      return storedAccessTokenExpiryIsValid();
    }
  }

  return new Auth();
}

const authService: IAuthService = createAuthService();

export default authService;
